import { useMemo, useState, useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { EditorActions } from '@atlaskit/editor-core';

import type { ExtensionSource } from '@confluence/change-edition/entry-points/PremiumExtensionsUpsell';

import { carouselExtensionType } from '../../carousel/carouselConstants';
import { linkCardsExtensionType } from '../../link-cards/linkCardsExtensionType';
import { spotlightExtensionType } from '../../spotlight/spotlightConstants';
import { linksMenuExtensionType } from '../../links-menu/linksMenuExtensionType';
import { RenderVariant } from '../../link-cards/linkCardsTypes';
import { hubSectionExtensionType } from '../../section/sectionConstants';

const ALLOWED_EXTENSIONS = [
	spotlightExtensionType,
	carouselExtensionType,
	linkCardsExtensionType,
	linksMenuExtensionType,
	hubSectionExtensionType,
];

type useCustomExtensionConfigPanelResult = {
	activeNodeLocalId: string | null;
	openConfigPanel: (localId: string) => void;
	closeConfigPanel: (localId?: string) => void;
	upsellModalSource: ExtensionSource | undefined;
	setUpsellModalSource: (source: ExtensionSource | undefined) => void;
};

export const useCustomExtensionConfigPanel = (
	editorActions: EditorActions,
): useCustomExtensionConfigPanelResult => {
	const [activeNodeLocalId, setActiveNodeLocalId] = useState<string | null>(null);

	// when upsellModalSource is undefined, we use it to close the upsell modal in ModalUpsellWrapper.
	const [upsellModalSource, setUpsellModalSource] = useState<ExtensionSource | undefined>(
		undefined,
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const openConfigPanel = useCallback(
		(localId: string) => {
			const { extensionType, extensionKey } =
				editorActions?.getNodeByLocalId(localId)?.toJSON().attrs || {};

			if (ALLOWED_EXTENSIONS.includes(extensionType)) {
				setActiveNodeLocalId(localId);
			}

			if (!!extensionKey && !!extensionType)
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'editExtension',
						source: `${extensionKey}ExtensionConfig`,
						attributes: {
							extensionType,
						},
					},
				}).fire();
		},
		[setActiveNodeLocalId, createAnalyticsEvent, editorActions],
	);

	const closeConfigPanel = useCallback(() => {
		const selectedNode = editorActions.getSelectedNode()?.toJSON();
		if (
			activeNodeLocalId &&
			selectedNode?.attrs?.localId === activeNodeLocalId &&
			selectedNode?.attrs?.extensionType === linkCardsExtensionType
		) {
			const parameters = selectedNode.attrs.parameters || {};

			if (parameters.renderVariant === RenderVariant.MANUAL) {
				delete parameters.filters;
				delete parameters.cardCount;
			}

			if (parameters.renderVariant === RenderVariant.DYNAMIC) {
				parameters.cards = [];
			}

			editorActions.replaceSelection(
				{
					...selectedNode,
					attrs: {
						...selectedNode.attrs,
						parameters: { ...parameters },
					},
				},
				true,
			);
		}

		setActiveNodeLocalId(null);
	}, [activeNodeLocalId, editorActions, setActiveNodeLocalId]);

	return useMemo(
		() => ({
			activeNodeLocalId,
			openConfigPanel,
			closeConfigPanel,
			upsellModalSource,
			setUpsellModalSource,
		}),
		[activeNodeLocalId, openConfigPanel, closeConfigPanel, upsellModalSource, setUpsellModalSource],
	);
};
